/**
 * Research API client for company profile data
 *
 * Fetches data from Phase 7A (Fundamentals), 7B (SEC Filings), and 7C (Insider/Institutional)
 */
import { apiClient } from './client'
import type {
  CompanyMetrics,
  FilingsResponse,
  FilingDetail,
  InsiderTradesResponse,
  InsiderSummary,
  InstitutionalHoldingsResponse,
  InstitutionalSummary,
} from '../types/research'
import type {
  AskRequest,
  AskResponse,
  SuggestionsResponse,
} from '../types/ai-qa'

// =============================================================================
// Fundamentals API (Phase 7A)
// =============================================================================

/**
 * Fetch all symbols that can be researched (have any data available).
 * Includes fundamentals, price history, and alert-tracked symbols.
 * @returns Array of ticker symbols
 */
export async function fetchResearchableSymbols(): Promise<string[]> {
  const response = await apiClient.get<string[]>('/api/fundamentals/researchable-symbols')
  return response.data
}

/**
 * Fetch company financial metrics (valuation, profitability, growth)
 * @param symbol Stock ticker symbol
 */
export async function fetchCompanyMetrics(symbol: string): Promise<CompanyMetrics> {
  const response = await apiClient.get<CompanyMetrics>(`/api/fundamentals/${encodeURIComponent(symbol)}`)
  return response.data
}

// =============================================================================
// SEC Filings API (Phase 7B)
// =============================================================================

export interface FetchSecFilingsParams {
  filingTypes?: string[] // Filter by filing type (10-K, 10-Q, 8-K)
  limit?: number
  offset?: number
}

/**
 * Fetch SEC filings for a symbol
 * @param symbol Stock ticker symbol
 * @param params Optional filters (filingTypes, limit, offset)
 */
export async function fetchSecFilings(
  symbol: string,
  params?: FetchSecFilingsParams
): Promise<FilingsResponse> {
  const queryParams: Record<string, string> = {}

  if (params?.filingTypes?.length) {
    queryParams.filing_types = params.filingTypes.join(',')
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit.toString()
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset.toString()
  }

  const response = await apiClient.get<FilingsResponse>(
    `/api/sec-filings/${encodeURIComponent(symbol)}`,
    { params: queryParams }
  )
  return response.data
}

/**
 * Fetch detailed filing with sections and summary
 * @param filingId Filing ID
 */
export async function fetchFilingDetail(filingId: number): Promise<FilingDetail> {
  const response = await apiClient.get<FilingDetail>(`/api/sec-filings/filing/${filingId}`)
  return response.data
}

// =============================================================================
// Insider Trades API (Phase 7C)
// =============================================================================

export interface FetchInsiderTradesParams {
  days?: number // Lookback period in days
  transactionTypes?: string[] // Filter by type (buy, sell, exercise, gift)
  limit?: number
  offset?: number
}

/**
 * Fetch insider trades for a symbol
 * @param symbol Stock ticker symbol
 * @param params Optional filters (days, transactionTypes, limit, offset)
 */
export async function fetchInsiderTrades(
  symbol: string,
  params?: FetchInsiderTradesParams
): Promise<InsiderTradesResponse> {
  const queryParams: Record<string, string> = {}

  if (params?.days !== undefined) {
    queryParams.days = params.days.toString()
  }
  if (params?.transactionTypes?.length) {
    queryParams.transaction_types = params.transactionTypes.join(',')
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit.toString()
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset.toString()
  }

  const response = await apiClient.get<InsiderTradesResponse>(
    `/api/insider-institutional/insider/${encodeURIComponent(symbol)}`,
    { params: queryParams }
  )
  return response.data
}

/**
 * Fetch insider activity summary for a symbol
 * @param symbol Stock ticker symbol
 * @param days Lookback period in days (default: 90)
 */
export async function fetchInsiderSummary(
  symbol: string,
  days?: number
): Promise<InsiderSummary> {
  const queryParams: Record<string, string> = {}

  if (days !== undefined) {
    queryParams.days = days.toString()
  }

  const response = await apiClient.get<InsiderSummary>(
    `/api/insider-institutional/insider/${encodeURIComponent(symbol)}/summary`,
    { params: queryParams }
  )
  return response.data
}

// =============================================================================
// Institutional Holdings API (Phase 7C)
// =============================================================================

export interface FetchInstitutionalHoldingsParams {
  period?: string // Quarter end date (YYYY-MM-DD)
  limit?: number
  offset?: number
}

/**
 * Fetch institutional holdings for a symbol
 * @param symbol Stock ticker symbol
 * @param params Optional filters (period, limit, offset)
 */
export async function fetchInstitutionalHoldings(
  symbol: string,
  params?: FetchInstitutionalHoldingsParams
): Promise<InstitutionalHoldingsResponse> {
  const queryParams: Record<string, string> = {}

  if (params?.period) {
    queryParams.period = params.period
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit.toString()
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset.toString()
  }

  const response = await apiClient.get<InstitutionalHoldingsResponse>(
    `/api/insider-institutional/institutional/${encodeURIComponent(symbol)}`,
    { params: queryParams }
  )
  return response.data
}

/**
 * Fetch institutional ownership summary for a symbol
 * @param symbol Stock ticker symbol
 * @param period Optional quarter end date (YYYY-MM-DD)
 */
export async function fetchInstitutionalSummary(
  symbol: string,
  period?: string
): Promise<InstitutionalSummary> {
  const queryParams: Record<string, string> = {}

  if (period) {
    queryParams.period = period
  }

  const response = await apiClient.get<InstitutionalSummary>(
    `/api/insider-institutional/institutional/${encodeURIComponent(symbol)}/summary`,
    { params: queryParams }
  )
  return response.data
}

// =============================================================================
// AI Q&A API (Phase 7E)
// =============================================================================

/**
 * Ask a question about companies using AI
 * @param request Question, symbols, and optional conversation history
 */
export async function askQuestion(request: AskRequest): Promise<AskResponse> {
  const response = await apiClient.post<AskResponse>('/api/research/ask', request)
  return response.data
}

/**
 * Get dynamically suggested questions for a symbol
 * @param symbol Stock ticker symbol
 */
export async function getSuggestions(symbol: string): Promise<SuggestionsResponse> {
  const response = await apiClient.get<SuggestionsResponse>(
    `/api/research/suggestions/${encodeURIComponent(symbol)}`
  )
  return response.data
}

// =============================================================================
// Aggregated Research API Object (follows holdings.ts pattern)
// =============================================================================

export const researchApi = {
  // Fundamentals
  getResearchableSymbols: fetchResearchableSymbols,
  getMetrics: fetchCompanyMetrics,

  // SEC Filings
  getFilings: fetchSecFilings,
  getFilingDetail: fetchFilingDetail,

  // Insider Trades
  getInsiderTrades: fetchInsiderTrades,
  getInsiderSummary: fetchInsiderSummary,

  // Institutional Holdings
  getInstitutionalHoldings: fetchInstitutionalHoldings,
  getInstitutionalSummary: fetchInstitutionalSummary,

  // AI Q&A
  askQuestion,
  getSuggestions,
}
